/**
 * Shared Google Maps Loader Hook
 *
 * Provides a single, consistent configuration for loading Google Maps API
 * across all components. This prevents the "Loader must not be called again
 * with different options" error.
 *
 * All components that need Google Maps should use this hook instead of
 * calling useJsApiLoader directly.
 */

import { useJsApiLoader } from '@react-google-maps/api';

// Shared configuration constants
const SHARED_LOADER_ID = 'google-maps-shared-loader';
// Include all commonly used libraries to avoid conflicts
// Note: 'marker' library is required for AdvancedMarkerElement (replacement for deprecated Marker)
const SHARED_LIBRARIES: ('maps' | 'places' | 'geometry' | 'marker')[] = [
  'maps',
  'places',
  'geometry',
  'marker',
];
const SHARED_VERSION = 'weekly';
const SHARED_LANGUAGE = 'en';
const SHARED_REGION = 'US';

/**
 * Get the Google Maps API key from environment variables
 * Tries NEXT_PUBLIC_MAP_API_KEY first, then falls back to NEXT_PUBLIC_GOOGLE_MAPS_API_KEY
 */
const getApiKey = (): string => {
  return process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || '';
};

/**
 * Shared hook for loading Google Maps API
 *
 * @param options - Optional overrides for specific use cases
 * @returns Object with isLoaded, loadError, and apiKey
 */
export const useGoogleMapsLoader = (options?: {
  libraries?: ('maps' | 'places' | 'geometry' | 'marker')[];
  apiKey?: string;
}) => {
  const apiKey = options?.apiKey || getApiKey();
  const libraries = options?.libraries || SHARED_LIBRARIES;

  const { isLoaded, loadError } = useJsApiLoader({
    id: SHARED_LOADER_ID,
    googleMapsApiKey: apiKey,
    libraries,
    version: SHARED_VERSION,
    language: SHARED_LANGUAGE,
    region: SHARED_REGION,
  });

  return {
    isLoaded,
    loadError,
    apiKey,
  };
};
